// --------------------- Import Globais ---------------------
import { useState, useEffect } from "react";
import { useMediaQuery } from "@mui/material";
// --------------------- Import Antd-Design ---------------------
import {
  Col,
  Flex,
  Row,
  MenuProps,
  Modal,
  Skeleton,
  Table,
  Typography,
  Button,
  Tooltip,
  Badge,
  Avatar,
  Tag,
} from "antd";
// --------------------- Import Material Icon ---------------------
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
// --------------------- Import Hooks ---------------------
import { useBuscarCompatibilidades } from "./hooks/useBuscarCompatibilidades";
import { useTodasCompatibilidades } from "./hooks/useTodasCompatibilidades";
import { useBuscarProcurasSemCompatibilidades } from "./hooks/useBuscarProcurasSemCompatibilidades";
// --------------------- Import Estilos ---------------------
import { Container } from "./styles";
// --------------------- Import Components ---------------------
import { ColumnsInteresse, ColumnsProcura } from "./components/Columns";
import ModalMaisInfo from "./components/ModalMaisInfo";
import ModalNotas from "./components/ModalNotas";
import ModalAlterarStatus from "./components/ModalAlterarStatus";
import ModalExcluir from "./components/ModalExcluir";
import ModalPesquisa from "./components/ModalPesquisa";
import ModalAndamentosCompatibilidade from "./components/ModalAndamentosCompatibilidade";
import ModalCondicoesPagamento from "./components/ModalCondicoesPagamento";
import ModalDescricaoNiveis from "./components/ModalDescricaoNiveis";
import {
  enviarPropostaCompatibilidadeService,
} from "../../services/enviarPropostaCompatibilidadeService";
import type { EnviarPropostaCompatibilidadeResponse } from "../../services/enviarPropostaCompatibilidadeService";
// --------------------- Import úteis ---------------------
import LoadingSpin from "../../uteis/LoadingSpin";
import { formatValorReal } from "../../uteis/formatValorReal";
import { useMessage } from "../../uteis/mensagens/useMessage";
import { verificarPermissao } from "../../services/verificarPermissao";
import { contarNiveis } from "./constants/contagemNiveis";

export default function ViewGerenciarCompatibilidades() {
  // Traz o estilo Title da biblioteca
  const { Title, Text } = Typography;
  const isMobile = useMediaQuery("(max-width:600px)");
  const [modoExibicao, setModoExibicao] = useState<
    "compatibilidades" | "semCompatibilidades"
  >("compatibilidades");

  const { compatibilidades, isLoading, buscarComp } =
    useBuscarCompatibilidades();
  const {
    buscarProcurasSemCompatibilidade,
    procurasSemCompatibilidade,
    totaisSemCompatibilidade,
    isLoading: isLoadingSemCompatibilidades,
    error: errorSemCompatibilidades,
    resetError: resetErrorSemCompatibilidades,
  } = useBuscarProcurasSemCompatibilidades();

  const [resultadoPesquisa, setResultadoPesquisa] = useState<any>();
  const [filtroNivel, setFiltroNivel] = useState<number | null>(null);
  const [tableFiltersResetKey, setTableFiltersResetKey] = useState(0);

  const resetPesquisa = () => setResultadoPesquisa(null);
  const limparFiltrosTabela = () => {
    setFiltroNivel(null);
    setTableFiltersResetKey((current) => current + 1);
  };

  const handleBuscarSemCompatibilidades = async () => {
    limparFiltrosTabela();
    const carregouComSucesso = await buscarProcurasSemCompatibilidade();

    if (carregouComSucesso) {
      setModoExibicao("semCompatibilidades");
    }
  };

  const handleVoltarCompatibilidades = () => {
    limparFiltrosTabela();
    setModoExibicao("compatibilidades");
  };

  // Função para filtrar compatibilidades por nível
  const filtrarPorNivel = (nivel: number) => {
    if (filtroNivel === nivel) {
      // Se clicar no mesmo nível, desativa o filtro
      setFiltroNivel(null);
    } else {
      setFiltroNivel(nivel);
    }
  };

  // Função para obter dados filtrados
  const getDadosFiltrados = () => {
    const dados = resultadoPesquisa ?? compatibilidades;
    if (!filtroNivel) return dados;
    
    return dados.map((item: any) => ({
      ...item,
      filhos: item.filhos?.filter((filho: any) => filho.nivel === filtroNivel) || [],
    })).filter((item: any) => item.filhos.length > 0);
  };

  const {
    todasCompatibilidades,
    isLoading: isLoadingTodasComp,
    error,
    success,
    resetError,
    resetSuccess,
  } = useTodasCompatibilidades();

  const [compatibilidadeId, setCompatibilidadeId] = useState<number | null>(
    null
  );

  const [compatibilidadeMaisInfoId, setCompatibilidadeMaisInfoId] = useState<
    number | null
  >(null);

  const [compatibilidadeOrigem, setCompatibilidadeOrigem] = useState<
    string | null
  >(null);

  const [compatibilidadeProcuraId, setCompatibilidadeProcuraId] = useState<
    number | null
  >(null);

  const [compatibilidadeOrigemProcura, setCompatibilidadeOrigemProcura] =
    useState<string | null>(null);

  const [compatibilidadeObservacao, setCompatibilidadeObservacao] = useState<
    string | null
  >(null);

  const [compatibilidadeValorDisp, setCompatibilidadeValorDisp] = useState<
    string | number | null
  >(null);

  const [compatibilidadeStatus, setCompatibilidadeStatus] = useState<
    string | null
  >(null);

  const [openModalCondicoesPagamento, setOpenModalCondicoesPagamento] =
    useState<boolean>(false);

  const handleModalCondicoesPagamento = () => {
    setOpenModalCondicoesPagamento(!openModalCondicoesPagamento);
  };

  const [openModalDescricaoNiveis, setOpenModalDescricaoNiveis] =
    useState<boolean>(false);

  const handleModalDescricaoNiveis = () => {
    setOpenModalDescricaoNiveis(!openModalDescricaoNiveis);
  };

  // Andamentos Compatibilidade
  const [openModalAndamentosCompatibilidade, setOpenModalAndamentosCompatibilidade] =
    useState<boolean>(false);

  const handleModalAndamentosCompatibilidade = () => {
    setOpenModalAndamentosCompatibilidade(!openModalAndamentosCompatibilidade);
  };

  // Mais Informações Procura
  const [openModalMaisInfoProcura, setOpenModalMaisInfoProcura] =
    useState<boolean>(false);

  const handleModalMaisInfoProcura = () => {
    setOpenModalMaisInfoProcura(!openModalMaisInfoProcura);
  };

  // Mais Informações Disponibiliza
  const [openModalMaisInfo, setOpenModalMaisInfo] = useState<boolean>(false);

  const handleModalMaisInfo = () => {
    setOpenModalMaisInfo(!openModalMaisInfo);
  };

  // Notas
  const [openModalNotas, setOpenModalNotas] = useState<boolean>(false);

  const handleModalNotas = () => {
    setOpenModalNotas(!openModalNotas);
  };

  // Alterar Status
  const [openModalAlterarStatus, setOpenModalAlterarStatus] =
    useState<boolean>(false);

  const handleModalAlterarStatus = () => {
    setOpenModalAlterarStatus(!openModalAlterarStatus);
  };

  // Alterar Status
  const [openModalExcluir, setOpenModalExcluir] = useState<boolean>(false);

  const handleModalExcluir = () => {
    setOpenModalExcluir(!openModalExcluir);
  };

  // Pesquisa
  const [openModalPesquisa, setOpenModalPesquisa] = useState<boolean>(false);

  const handleModalPesquisa = () => {
    setOpenModalPesquisa(!openModalPesquisa);
  };

  const mostrarResultadoEnviarProposta = (
    response: EnviarPropostaCompatibilidadeResponse
  ) => {
    const nomeCompleto = [response.nome, response.sobrenome]
      .filter(Boolean)
      .join(" ");

    if (!response.criada && !response.jaExistia) {
      Modal.warning({
        title: "Não foi possível enviar a proposta",
        content: (
          <div>
            <p>
              {response.motivo ||
                "A compatibilidade não permite criar proposta agora."}
            </p>
            {response.statusAtual ? <p>Status atual: {response.statusAtual}</p> : null}
          </div>
        ),
      });
      return;
    }

    Modal.success({
      title: response.jaExistia ? "Proposta já existente" : "Proposta enviada",
      content: (
        <div>
          <p>
            {response.jaExistia
              ? "Já existia uma solicitação de reserva para essa compatibilidade e a validade foi renovada."
              : "A solicitação de reserva foi criada com sucesso a partir da compatibilidade."}
          </p>
          {response.solicitacaoReservaId ? (
            <p>Solicitação de reserva: #{response.solicitacaoReservaId}</p>
          ) : null}
          {nomeCompleto ? <p>Cliente: {nomeCompleto}</p> : null}
          {response.celular ? <p>Celular: {response.celular}</p> : null}
          {response.valorPropostoClienteInicial != null ? (
            <p>
              Valor proposto inicial:{" "}
              {formatValorReal(response.valorPropostoClienteInicial)}
            </p>
          ) : null}
        </div>
      ),
    });
  };

  const possuiValorDisponivel = (valor: string | number | null) => {
    if (valor === null || valor === undefined) {
      return false;
    }

    if (typeof valor === "number") {
      return valor > 0;
    }

    const valorLimpo = valor.trim();

    if (!valorLimpo) {
      return false;
    }

    const valorNumerico = Number(
      valorLimpo.replace(/\./g, "").replace(",", ".").replace(/[^\d.-]/g, "")
    );

    if (Number.isNaN(valorNumerico)) {
      return false;
    }

    return valorNumerico > 0;
  };

  useEffect(() => {
    const verificarAcesso = () => {
      verificarPermissao("OPERADOR");
    };
    verificarAcesso();
  }, []);

  // OnClick das opções e funções que serão realizadas do item Ações
  const onClick: MenuProps["onClick"] = async ({ key }) => {
    switch (key) {
      // Mais Informações
      case "1":
        handleModalMaisInfo();
        break;

      case "2":
        // Notas
        handleModalNotas();
        break;
      case "3":
        //Alterar Status
        handleModalAlterarStatus();
        break;
      case "4":
        // Ver Andamentos
        handleModalAndamentosCompatibilidade();
        break;
      case "5":
        // Excluir
        handleModalExcluir();
        break;
      case "6":
        // Condições de pagamento
        handleModalCondicoesPagamento();
        break;
      case "7":
        if (!compatibilidadeId) {
          showError("Não foi possível identificar a compatibilidade.");
          break;
        }

        if (!possuiValorDisponivel(compatibilidadeValorDisp)) {
          showWarning(
            "E necessario que o periodo tenha um valor disponivel para enviar a proposta."
          );
          break;
        }

        try {
          const response = await enviarPropostaCompatibilidadeService(
            compatibilidadeId
          );

          if (!response) {
            showError("Não foi possível enviar a proposta.");
            break;
          }

          mostrarResultadoEnviarProposta(response);

          if (response.criada || response.jaExistia) {
            buscarComp();
          }
        } catch (error) {
          showError("Não foi possível enviar a proposta.");
        }
        break;
    }
  };

  const onClickProcura: MenuProps["onClick"] = async ({ key }) => {
    switch (key) {
      // Mais Informações
      case "1":
        handleModalMaisInfoProcura();
        break;
    }
  };
  const columnsProcura = ColumnsProcura(
    onClickProcura,
    setCompatibilidadeProcuraId,
    setCompatibilidadeOrigemProcura
  );
  const columnsInteresse = ColumnsInteresse(
    onClick,
    setCompatibilidadeId,
    setCompatibilidadeMaisInfoId,
    setCompatibilidadeOrigem,
    setCompatibilidadeObservacao,
    setCompatibilidadeStatus,
    setCompatibilidadeValorDisp
  );

  // Exibe a Mensagem de  Erro
  const { showSuccess, showError, showWarning, contextHolder } = useMessage();

  // Estados para controlar as exibições
  const [errorDisplayed, setErrorDisplayed] = useState(false);
  const [successDisplayed, setSuccessDisplayed] = useState(false);
  const [errorSemCompatibilidadesDisplayed, setErrorSemCompatibilidadesDisplayed] =
    useState(false);

  useEffect(() => {
    if (success && !successDisplayed) {
      showSuccess(success);
      setSuccessDisplayed(true);
      // Limpa estado de sucesso após 3 segundos e recarrega a página
      setTimeout(() => {
        setSuccessDisplayed(false);
        resetSuccess();
        buscarComp();
        if (modoExibicao === "semCompatibilidades") {
          buscarProcurasSemCompatibilidade();
        }
      }, 2000);
    }
    // Exibe a mensagem de erro
    if (error && !errorDisplayed) {
      showError(error);
      setErrorDisplayed(true);

      // Limpa estado de error após 3 segundos
      setTimeout(() => {
        setErrorDisplayed(false);
        resetError();
      }, 3000);
    }
  }, [success, successDisplayed, error, errorDisplayed]);

  useEffect(() => {
    if (
      errorSemCompatibilidades &&
      !errorSemCompatibilidadesDisplayed
    ) {
      showError(errorSemCompatibilidades);
      setErrorSemCompatibilidadesDisplayed(true);

      setTimeout(() => {
        setErrorSemCompatibilidadesDisplayed(false);
        resetErrorSemCompatibilidades();
      }, 3000);
    }
  }, [
    errorSemCompatibilidades,
    errorSemCompatibilidadesDisplayed,
    resetErrorSemCompatibilidades,
    showError,
  ]);

  const contagem = contarNiveis(resultadoPesquisa ?? compatibilidades);
  const mostrandoSemCompatibilidades = modoExibicao === "semCompatibilidades";
  const dadosTabela = mostrandoSemCompatibilidades
    ? procurasSemCompatibilidade
    : getDadosFiltrados();

  // Aqui você pode mapear os níveis para ícones diferentes, se quiser
  const icones: Record<number, React.ReactNode> = {
    1: <span>Nível 1</span>,
    2: <span>Nível 2</span>,
    3: <span>Nível 3</span>,
    4: <span>Nível 4</span>,
    5: <span>Nível 5</span>,
    6: <span>Nível 6</span>,
    7: <span>Nível 7</span>,
    8: <span>Nível 8</span>,
    9: <span>Nível 9</span>,
  };

  // Opcional: cores progressivas por nível
  const coresNiveis: Record<number, string> = {
    1: "green",
    2: "#13c2c2",        // Cyan mais escuro e visível
    3: "blue",
    4: "#1765ad",        // Azul mais escuro e visível
    5: "purple",
    6: "magenta",
    7: "orange",
    8: "volcano",
    9: "red",
  };

  return (
    <>
      {isLoading ? (
        <Skeleton active />
      ) : isLoadingTodasComp ? (
        <LoadingSpin
          message="Buscando novas compatibilidades..."
          description="Esta ação pode levar alguns instantes. Aguarde, por favor..."
        />
      ) : (
        <Container>
          {contextHolder}
          <Row>
            <Col span={5}>
              <Flex align="center" justify="center" style={{ marginTop: 15 }}>
                <a href={`/inicio`}>
                  
                </a>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" justify="center">
                <HomeIcon color="primary" fontSize="large" />
                <Title
                  style={{
                    marginLeft: 10,
                    marginTop: 10,
                    fontSize: isMobile ? "1.5rem" : "2rem",
                    color: "rgb(0, 54, 68)",
                  }}
                  level={3}
                >
                  Gerenciar Compatibilidades
                </Title>
              </Flex>
            </Col>
          </Row>
          <Row>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end", // Alinha o conteúdo à direita
                alignItems: "center",
                height: "auto",
                width: "90%", // Garante que ocupe toda a largura disponível
                gap: "20px", // Espaço entre os botões
              }}
            >
              <Button
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "auto",
                }}
                onClick={() => {
                  todasCompatibilidades();
                }}
                type="primary"
              >
                Atualizar
              </Button>
              <Button
                onClick={handleBuscarSemCompatibilidades}
                loading={isLoadingSemCompatibilidades}
                type={mostrandoSemCompatibilidades ? "default" : "primary"}
              >
                Sem Compatibilidades
              </Button>
              {mostrandoSemCompatibilidades ? (
                <Button onClick={handleVoltarCompatibilidades}>
                  Compatibilidades
                </Button>
              ) : resultadoPesquisa ? (
                <Button onClick={() => resetPesquisa()} type="primary">
                  <FilterAltOffIcon />
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    handleModalPesquisa();
                  }}
                  type="primary"
                >
                  Pesquisa
                </Button>
              )}
              <Button onClick={limparFiltrosTabela}>
                <FilterAltOffIcon />
                Limpar filtros
              </Button>
              {mostrandoSemCompatibilidades ? (
                <Flex
                  justify="flex-end"
                  align="center"
                  gap="8px"
                  style={{ flexWrap: "wrap" }}
                >
                  <Text strong>Procuras sem compatibilidades</Text>
                  <Tag color="blue">Total: {totaisSemCompatibilidade.total}</Tag>
                  <Tag color="gold">
                    INT: {totaisSemCompatibilidade.totalINT}
                  </Tag>
                  <Tag color="purple">
                    POST: {totaisSemCompatibilidade.totalPOST}
                  </Tag>
                </Flex>
              ) : null}
              {!mostrandoSemCompatibilidades ? (
                <Flex justify="flex-end" align="center" gap="8px">
                <Tooltip title="Ver descrição de cada nível">
                  <Button
                    shape="circle"
                    onClick={handleModalDescricaoNiveis}
                    type="default"
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </Button>
                </Tooltip>
                {Object.entries(contagem)
                  .sort((a, b) => Number(a[0]) - Number(b[0])) // ordena do menor para maior
                  .map(([nivel, quantidade]) => {
                    const isActive = filtroNivel === Number(nivel);
                    return (
                      <Tooltip key={nivel} title={`Clique para filtrar por Nível ${nivel}`}>
                        <div
                          onClick={() => filtrarPorNivel(Number(nivel))}
                          style={{
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            transform: isActive ? "scale(1.05)" : "scale(1)",
                          }}
                        >
                          <Badge count={quantidade} style={{ marginRight: 10 }}>
                            <Avatar
                              shape="square"
                              size="large"
                              style={{
                                backgroundColor: isActive
                                  ? coresNiveis[Number(nivel)] || "grey"
                                  : "transparent",
                                color: isActive
                                  ? "white"
                                  : coresNiveis[Number(nivel)] || "grey",
                                border: `${isActive ? "2px" : "1px"} solid ${
                                  coresNiveis[Number(nivel)] || "grey"
                                }`,
                                marginRight: 10,
                                fontWeight: isActive ? "bold" : "normal",
                              }}
                            >
                              {icones[Number(nivel)] || <span>{nivel}</span>}
                            </Avatar>
                          </Badge>
                        </div>
                      </Tooltip>
                    );
                  })}
                {filtroNivel && (
                  <Button
                    size="small"
                    onClick={() => setFiltroNivel(null)}
                    style={{ marginLeft: "8px" }}
                  >
                    <FilterAltOffIcon />
                  </Button>
                )}
                </Flex>
              ) : null}
            </div>
            <Col span={isMobile ? 5 : 1}></Col>
            <Col
              span={22}
              style={{
                marginTop: isMobile ? 15 : 15,
                marginBottom: isMobile ? 30 : 50,
              }}
            >
              <Flex
                style={{
                  width: isMobile ? "auto" : "100%",
                  overflow: "auto",
                  marginLeft: 30,
                }}
              >
                <Table
                  key={`compatibilidades-${tableFiltersResetKey}`}
                  columns={columnsProcura}
                  dataSource={dadosTabela}
                  loading={
                    mostrandoSemCompatibilidades
                      ? isLoadingSemCompatibilidades
                      : isLoading
                  }
                  rowKey="chave"
                  locale={{
                    emptyText: mostrandoSemCompatibilidades
                      ? "Nenhuma procura sem compatibilidades encontrada."
                      : "Nenhuma compatibilidade encontrada.",
                  }}
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    width: isMobile ? "auto" : "100%",
                    maxWidth: isMobile ? "none" : "100%",
                  }}
                  expandable={
                    mostrandoSemCompatibilidades
                      ? undefined
                      : {
                          expandedRowRender: (record) => (
                            <Table
                              key={`compatibilidades-filhos-${tableFiltersResetKey}-${record.chave}`}
                              columns={columnsInteresse}
                              dataSource={record.filhos}
                              pagination={false}
                              style={{
                                alignItems: "center",
                                justifyContent: "center",
                                width: isMobile ? "auto" : "100%",
                                maxWidth: isMobile ? "none" : "100%",
                              }}
                              rowKey="id"
                              size="small"
                            />
                          ),
                        }
                  }
                />
              </Flex>
            </Col>
          </Row>
        </Container>
      )}
      <ModalMaisInfo
        isModalOpen={openModalMaisInfoProcura}
        handleFechar={handleModalMaisInfoProcura}
        compatibilidadeId={compatibilidadeProcuraId}
        compatibilidadeOrigem={compatibilidadeOrigemProcura}
      />
      <ModalMaisInfo
        isModalOpen={openModalMaisInfo}
        handleFechar={handleModalMaisInfo}
        compatibilidadeId={compatibilidadeMaisInfoId}
        compatibilidadeOrigem={compatibilidadeOrigem}
      />
      <ModalNotas
        isModalOpen={openModalNotas}
        handleFechar={handleModalNotas}
        compatibilidadeId={compatibilidadeId}
      />
      <ModalAlterarStatus
        isModalOpen={openModalAlterarStatus}
        handleFechar={handleModalAlterarStatus}
        compatibilidadeId={compatibilidadeId!}
        buscarComp={buscarComp}
        compatibilidadeStatus={compatibilidadeStatus!}
      />
      <ModalExcluir
        isModalOpen={openModalExcluir}
        handleFechar={handleModalExcluir}
        compatibilidadeId={compatibilidadeId}
        buscarComp={buscarComp}
      />
      <ModalPesquisa
        isModalOpen={openModalPesquisa}
        handleFechar={handleModalPesquisa}
        onResultadoPesquisa={(data: any) => setResultadoPesquisa(data)}
      />
      <ModalAndamentosCompatibilidade
        isModalOpen={openModalAndamentosCompatibilidade}
        handleFechar={handleModalAndamentosCompatibilidade}
        compatibilidadeId={compatibilidadeId}
      />
      <ModalCondicoesPagamento
        isModalOpen={openModalCondicoesPagamento}
        handleFechar={handleModalCondicoesPagamento}
        compatibilidadeId={compatibilidadeId}
      />
      <ModalDescricaoNiveis
        isModalOpen={openModalDescricaoNiveis}
        handleFechar={handleModalDescricaoNiveis}
      />
    </>
  );
}
